<template>
    <app-title />
    <!-- eslint-disable max-len -->
    <div id="reader"
         ref="el"
         class="bg-black h-screen w-screen overflow-hidden"
         :class="{'cursor-none': !isCursorVisible}">
        <nav class="fixed inset-x-0 top-0 h-12 bg-black/50 flex items-center z-40 px-4 transition-transform"
             :class="{'-translate-y-full': !isNavVisible}">
            <div class="flex items-center space-x-2 sm:space-x-4 mr-auto">
                <HeadlessMenu as="div" class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <computer-desktop-icon class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute left-0 z-10 mt-2 w-48 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <MenuItem v-for="size in sizes"
                                          v-slot="{ active }"
                                          :key="size.key">
                                    <button :class="[active ? 'bg-gray-100' : '', currentSize.key === size.key ? 'font-semibold text-black' : 'text-gray-700', 'flex items-center w-full px-4 py-2 text-sm']"
                                            @click="selectSize(size)">
                                        <check-icon v-if="currentSize.key === size.key"
                                                    class="h-3 w-3 mr-2" />
                                        {{ size.value }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
                <HeadlessMenu v-if="render.languages.length"
                              as="div"
                              class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <arrow-path-icon v-if="currentLanguage.id !== preloadLanguage.id"
                                             class="h-8 w-8 animate-spin" />
                            <chat-bubble-bottom-center-text-icon v-else class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute left-0 z-10 mt-2 w-48 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <MenuItem v-for="language in render.languages"
                                          v-slot="{ active }"
                                          :key="language.id">
                                    <button :class="[active ? 'bg-gray-100' : '', currentLanguage.code === language.code ? 'font-semibold text-black' : 'text-gray-700', 'flex items-center w-full px-4 py-2 text-sm']"
                                            @click="selectLanguage(language)">
                                        <check-icon v-if="currentLanguage.code === language.code"
                                                    class="h-3 w-3 mr-2" />
                                        {{ language.name }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
            </div>
            <div class="flex items-center space-x-2 ml-2 sm:space-x-4 sm:ml-4">
                <HeadlessMenu as="div" class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <share-icon class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.facebook.com/thelegionforever"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
                                        </svg>
                                        Facebook
                                    </a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.youtube.com/channel/UCmStOaqbcfkejK9BKm3VjvA"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
                                        </svg>
                                        YouTube
                                    </a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.instagram.com/thelegionforever/"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
                                        </svg>
                                        Instagram
                                    </a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
                <template v-if="isStarted">
                    <button v-if="backwardStep"
                            type="button"
                            class="reader-link"
                            @click="backward()">
                        <backward-icon class="h-8 w-8" />
                    </button>
                    <button type="button"
                            class="reader-link"
                            @click="restart()">
                        <arrow-path-icon class="h-8 w-8" />
                    </button>
                    <button v-if="forwardStep"
                            type="button"
                            class="reader-link"
                            @click="forward()">
                        <forward-icon class="h-8 w-8" />
                    </button>
                    <button v-if="isPlaying"
                            type="button"
                            class="reader-link"
                            @click="pause()">
                        <pause-icon class="h-8 w-8" />
                    </button>
                    <button v-else
                            type="button"
                            class="reader-link"
                            @click="play()">
                        <play-icon class="h-8 w-8" />
                    </button>
                    <button v-if="isAutoPlaying"
                            type="button"
                            class="reader-link"
                            @click="stopAutoPlay()">
                        <play-circle-icon class="h-8 w-8 text-blue-500" />
                    </button>
                    <button v-else
                            type="button"
                            class="reader-link"
                            @click="autoPlay()">
                        <play-circle-icon class="h-8 w-8" />
                    </button>
                </template>
                <HeadlessMenu v-if="render.tracks.length"
                              as="div"
                              class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <speaker-wave-icon v-if="isAudioEnabled" class="h-8 w-8" />
                            <speaker-x-mark-icon v-else class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <div class="px-4 py-2 flex items-center">
                                    <button v-if="isAudioEnabled"
                                            class="flex items-center text-gray-700 mr-2"
                                            @click="stopAudio()">
                                        <pause-icon class="h-5 w-5" />
                                    </button>
                                    <button v-else
                                            class="flex items-center text-gray-700 mr-2"
                                            @click="playAudio()">
                                        <play-icon class="h-5 w-5" />
                                    </button>
                                    <div class="progress h-2 flex-1 bg-gray-300 rounded-full"
                                         :class="{'pointer-events-none': !isAudioEnabled}"
                                         @click.stop="setVolume($event)">
                                        <div class="h-2 rounded-full"
                                             :class="{'bg-gray-600': !isAudioEnabled, 'bg-blue-500': isAudioEnabled}"
                                             :style="{width: volumePosition()}"></div>
                                    </div>
                                </div>
                                <MenuItem v-for="track in render.tracks"
                                          v-slot="{ active }"
                                          :key="track.id">
                                    <button :class="[active ? 'bg-gray-100' : '', currentTrack.id === track.id ? 'font-semibold text-black' : 'text-gray-700', 'flex items-center w-full px-4 py-2 text-sm']"
                                            @click="selectTrack(track)">
                                        {{ track.name }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
                <button type="button"
                        class="reader-link"
                        @click="fullscreen()">
                    <arrows-pointing-out-icon class="h-8 w-8"
                                              :class="{'text-blue-500': isFullscreen}" />
                </button>
            </div>
        </nav>
        <template v-if="isStarted">
            <button v-if="prevStep || hasPrevLabel()"
                    type="button"
                    class="fixed top-1/2 left-4 -translate-y-1/2 transition-transform text-white z-30"
                    :class="{'-translate-x-20': isAutoPlaying || !isLeftArrowVisible}"
                    @click="prev()">
                <chevron-left-icon class="h-16 w-16" />
            </button>
            <button v-if="nextStep || hasNextLabel()"
                    type="button"
                    class="fixed top-1/2 right-4 -translate-y-1/2 transition-transform text-white z-30"
                    :class="{'translate-x-20': isAutoPlaying || !isRightArrowVisible || isNextLocked}"
                    @click="next()">
                <span :class="{'block bg-black/50 text-sky-200 rounded-lg shadow shadow-sky-200 animate-pulse': isLoopStep && !currentLabelIndex}">
                    <chevron-right-icon class="h-16 w-16" />
                </span>
            </button>
        </template>
        <Vue3Lottie v-if="!isStarted"
                    :loop="loaderAnimations[currentLoaderIndex].loop"
                    :animation-link="loaderAnimations[currentLoaderIndex].animationLink"
                    @on-complete="currentLoaderIndex < loaderAnimations.length - 1 && currentLoaderIndex++" />
        <div v-if="!isLoaded && isStarted" class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white z-30">
            <arrow-path-icon class="h-16 w-16 animate-spin" />
        </div>
        <button v-if="isLoaded && (!isStarted || isSizeSelected)"
                type="button"
                class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white z-30"
                @click="play()">
            <play-icon class="h-16 w-16 animate-pulse" />
        </button>
        <audio v-if="currentTrack"
               class="hidden"
               loop>
            <source :src="currentTrack.path" type="audio/mpeg">
        </audio>
        <div class="reader-container">
            <div v-if="isStarted && render.steps[currentIndex] && render.steps[currentIndex].actions.length"
                 class="reader-item max-h-screen mx-auto grid grid-cols-12 z-30"
                 :style="{width: `${videoWidth}px`}">
                <template v-for="(action, index) in actions"
                          :key="index">
                    <button v-if="action"
                            :id="`position-${action.position}`"
                            type="button"
                            class="w-full h-full"
                            @click="currentAction = action"></button>
                    <div v-else
                         :id="`position-${index + 1}`"
                         class="w-full h-full"></div>
                </template>
            </div>
            <template v-for="label in labels"
                      :key="label.id">
                <div class="reader-item bg-contain bg-no-repeat bg-center z-20"
                     :class="{'opacity-0': !isCurrentLabel(label) || label.language_id !== currentLanguage.id}"
                     :style="{backgroundImage: `url('${label.path}')`}"></div>
            </template>
            <video class="reader-item max-h-screen w-full z-10"
                   disablePictureInPicture="true"
                   muted
                   playsinline>
                <source :src="sourcePath(currentSize)" type="video/mp4">
            </video>
        </div>
        <div class="reader-thumbnails"
             :class="{'translate-y-full': !isStarted || !isThumbVisible}">
            <Splide ref="thumb"
                    :options="thumbOptions">
                <SplideSlide v-for="(step, index) in thumbSteps"
                             :key="step.id"
                             @click="thumbClick(index)">
                    <span v-if="currentThumbIndex === index"
                          class="absolute inset-0 border-2 border-blue-500"></span>
                    <span class="flex justify-center text-sm text-white font-bold">
                        {{ index + 1 }}
                    </span>
                    <img :src="step.path_thumb" alt="">
                </SplideSlide>
            </Splide>
        </div>
    </div>
    <TransitionRoot as="template"
                    :show="!!currentAction">
        <HeadlessDialog as="div"
                        class="relative z-20"
                        @close="currentAction = undefined">
            <TransitionChild as="template"
                             enter="ease-out duration-300"
                             enter-from="opacity-0"
                             enter-to="opacity-100"
                             leave="ease-in duration-200"
                             leave-from="opacity-100"
                             leave-to="opacity-0">
                <div class="fixed inset-0 bg-black/50 transition-opacity"></div>
            </TransitionChild>
            <!-- eslint-disable max-len -->
            <div class="fixed z-20 inset-0 overflow-y-auto">
                <div class="flex items-center justify-center min-h-full text-center lg:relative">
                    <TransitionChild as="template"
                                     enter="ease-out duration-300"
                                     enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                     enter-to="opacity-100 translate-y-0 sm:scale-100"
                                     leave="ease-in duration-200"
                                     leave-from="opacity-100 translate-y-0 sm:scale-100"
                                     leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel v-show="currentAction"
                                     class="max-w-xl overflow-hidden transform transition-all lg:transition-opacity lg:absolute"
                                     :style="{
                                         top: currentAction && (currentAction.direction === 0 || currentAction.direction === 1)
                                             ? `${actionDialogY}px`
                                             : 'auto',
                                         left: currentAction && (currentAction.direction === 0 || currentAction.direction === 2)
                                             ? `${actionDialogX}px`
                                             : 'auto',
                                         right: currentAction && (currentAction.direction === 1 || currentAction.direction === 3)
                                             ? `calc(100vw - ${actionDialogX}px)`
                                             : 'auto',
                                         bottom: currentAction && (currentAction.direction === 2 || currentAction.direction === 3)
                                             ? `calc(100vh - ${actionDialogY}px)`
                                             : 'auto'
                                     }">
                            <img v-if="currentAction && !currentAction.path.endsWith('.mp4')"
                                 :src="currentAction.path">
                            <video v-else-if="currentAction"
                                   class="w-full"
                                   disablePictureInPicture="true"
                                   autoplay
                                   muted
                                   loop
                                   playsinline>
                                <source :src="currentAction.path" type="video/mp4">
                            </video>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
            <!-- eslint-enable max-len -->
        </HeadlessDialog>
    </TransitionRoot>
    <!-- eslint-enable max-len -->
</template>

<script setup>
import {
    ArrowPathIcon,
    ArrowsPointingOutIcon,
    BackwardIcon,
    ChatBubbleBottomCenterTextIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ComputerDesktopIcon,
    ForwardIcon,
    PlayIcon,
    PlayCircleIcon,
    PauseIcon,
    ShareIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon
} from '@heroicons/vue/24/outline';

import {
    Menu as HeadlessMenu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog as HeadlessDialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot
} from '@headlessui/vue';

import Screenfull from 'screenfull';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { Vue3Lottie } from 'vue3-lottie';

import {
    defineProps,
    ref,
    computed,
    watch,
    onMounted
} from 'vue';

import useMetric from '../../use/useMetric';

require('swiped-events');

const {
    app,
    isForceFirstPlay,
    loopType,
    sizes,
    render,
    defaultLanguage,
    time
} = defineProps({
    app: {
        type: Object,
        required: true
    },

    isForceFirstPlay: {
        type: Boolean,
        required: true
    },

    loopType: {
        type: Number,
        required: true
    },

    loaderAnimations: {
        type: Array,
        required: true
    },

    sizes: {
        type: Array,
        required: true
    },

    render: {
        type: Object,
        required: true
    },

    defaultLanguage: {
        type: String,
        default: ''
    },

    time: {
        type: Number,
        default: -1
    }
});

let recommendedSize = sizes[0];

const refHeight = _.min([
    window.screen.width,
    window.screen.height
]);

_.forEachRight(sizes, size => {
    if (size.key < refHeight) {
        return true;
    }

    recommendedSize = size;

    return false;
});

const isRead = JSON.parse(window.localStorage.getItem(`_read_${render.id}`) || 'false');

window.localStorage.setItem(`_read_${render.id}`, JSON.stringify(true));

const {
    startMetric,
    sendMetric,
    addMetric,
    addVideoAction
} = useMetric();

const isLoaded = ref(false);
const isStarted = ref(false);
const isPlaying = ref(false);
const isAutoPlaying = ref(false);
const isAudioEnabled = ref(true);
const isAudioPlaying = ref(false);
const isLooping = ref(false);
const isLoopStep = ref(false);
const isFullscreen = ref(false);
const isNavVisible = ref(true);
const isLeftArrowVisible = ref(true);
const isRightArrowVisible = ref(true);
const isCursorVisible = ref(true);
const isNextLocked = ref(false);
const isSizeSelected = ref(false);
const isThumbVisible = ref(true);
const isTouchOnlyDevice = window.matchMedia('(any-hover: none)').matches;
const currentSize = ref(recommendedSize);
const currentIndex = ref(undefined);
const currentLabelIndex = ref(undefined);
const currentLanguage = ref();
const currentTrack = ref();
const currentThumbIndex = ref(undefined);
const preloadLanguage = ref();
const nextStep = ref(undefined);
const prevStep = ref(undefined);
const backwardStep = ref(undefined);
const forwardStep = ref(undefined);
const firstInSequence = ref(undefined);
const audioElement = ref(undefined);
const videoElement = ref(undefined);
const videoWidth = ref(1920);
const navTimeout = ref(undefined);
const leftArrowTimeout = ref(undefined);
const rightArrowTimeout = ref(undefined);
const thumbTimeout = ref(undefined);
const cursorTimeout = ref(undefined);
const labelTimeout = ref(undefined);
const timeUpdateAnimation = ref(undefined);
const goToTime = ref(time);
const volume = ref(0.5);
const el = ref();
const currentLoaderIndex = ref(0);

if (render.languages.length) {
    let first;

    if (defaultLanguage) {
        first = _.find(
            render.languages, current => defaultLanguage === current.code
        );
    } else {
        [first] = render.languages;
    }

    currentLanguage.value = first;
    preloadLanguage.value = first;
}

if (render.tracks.length) {
    const [first] = render.tracks;

    currentTrack.value = first;
}

const currentAction = ref(undefined);
const actionDialogX = ref(0);
const actionDialogY = ref(0);

const controlsTimer = 3000;
const labelTimer = 3000;

const labels = computed(() => _.filter(
    _.union(..._.map(render.steps, 'labels')),
    label => label.id !== 0
        && (label.language_id === preloadLanguage.value.id
            || label.language_id === currentLanguage.value.id)
));

const currentLabels = computed(() => _.filter(
    render.steps[currentIndex.value].labels,
    label => label.language_id === 0 || label.language_id === currentLanguage.value.id
));

const actions = computed(() => {
    const value = Array.from(Array(96));

    const filteredActions = _.filter(
        _.get(render.steps[currentIndex.value], 'actions', []),
        action => action.language_id === currentLanguage.value.id
    );

    _.forEach(filteredActions, action => {
        value[action.position - 1] = action;
    });

    return value;
});

const thumbSteps = computed(() => _.uniqBy(render.steps, 'sequence'));

const thumb = ref();
const thumbOptions = {
    fixedWidth: 104,
    fixedHeight: 78,
    isNavigation: true,
    gap: 10,
    focus: 'center',
    pagination: false,
    keyboard: false,
    arrows: false,
    cover: true,
    dragMinThreshold: {
        mouse: 4,
        touch: 10
    },
    breakpoints: {
        640: {
            fixedWidth: 66,
            fixedHeight: 58
        }
    }
};

const hasLabel = () => !_.isUndefined(currentLabelIndex.value);

const isCurrentLabel = label => hasLabel()
        && label === currentLabels.value[currentLabelIndex.value];

const hasPrevLabel = () => hasLabel() && currentLabelIndex.value > 0;

const hasNextLabel = () => hasLabel()
    && currentLabelIndex.value < currentLabels.value.length - 1;

const initAudio = () => {
    audioElement.value = el.value.querySelector('audio');
    audioElement.value.volume = volume.value;

    audioElement.value.addEventListener('play', () => {
        isAudioPlaying.value = true;
    }, false);

    audioElement.value.addEventListener('pause', () => {
        isAudioPlaying.value = false;
    }, false);
};

const timeUpdate = () => {
    if (goToTime.value > -1) {
        videoElement.value.currentTime = goToTime.value;
        goToTime.value = -1;

        return;
    }

    currentIndex.value = _.findLastIndex(
        render.steps, step => videoElement.value.currentTime >= step.start
    );

    isLoopStep.value = render.steps[currentIndex.value].type === loopType;

    if ((!isAutoPlaying.value || hasNextLabel()) && isLoopStep.value) {
        const currentStep = render.steps[currentIndex.value];
        const end = currentStep.end - 1;

        if (isLooping.value && videoElement.value.currentTime < end) {
            isLooping.value = false;
        }

        if (!isLooping.value && videoElement.value.currentTime >= end) {
            isLooping.value = true;
            videoElement.value.currentTime -= (end - currentStep.start);
        }
    }

    if (currentIndex.value === render.steps.length - 1) {
        sendMetric(app.locale, currentIndex.value);
    }

    videoWidth.value = videoElement.value.videoWidth * (
        videoElement.value.offsetHeight / videoElement.value.videoHeight
    );

    if (currentAction.value) {
        const {
            x,
            y,
            width,
            height
        } = document.getElementById(`position-${currentAction.value.origin}`).getBoundingClientRect();

        if (currentAction.value.direction === 0) {
            actionDialogX.value = x;
            actionDialogY.value = y;
        } else if (currentAction.value.direction === 1) {
            actionDialogX.value = x + width;
            actionDialogY.value = y;
        } else if (currentAction.value.direction === 2) {
            actionDialogX.value = x;
            actionDialogY.value = y + height;
        } else if (currentAction.value.direction === 3) {
            actionDialogX.value = x + width;
            actionDialogY.value = y + height;
        }
    }

    if (isPlaying.value) {
        // eslint-disable-next-line no-use-before-define
        startTimeUpdateTimer();
    }
};

const startTimeUpdateTimer = () => {
    timeUpdateAnimation.value = window.requestAnimationFrame(timeUpdate);
};

const resetTimeUpdateTimer = () => {
    window.cancelAnimationFrame(timeUpdateAnimation.value);
    startTimeUpdateTimer();
};

const onVideoLoaded = () => {
    isLoaded.value = true;
};

const onPlayVideo = () => {
    isPlaying.value = true;
    isSizeSelected.value = false;

    if (!isStarted.value) {
        isStarted.value = true;

        startMetric(render.id, app.locale);
        addMetric('play');

        if (isAudioEnabled.value && currentTrack.value) {
            audioElement.value.play();
        }
    }

    startTimeUpdateTimer();
};

const onPauseVideo = () => {
    isPlaying.value = false;
    isAutoPlaying.value = false;
};

const initVideo = () => {
    videoElement.value = el.value.querySelector('video');
    videoElement.value.addEventListener('loadedmetadata', onVideoLoaded, false);
    videoElement.value.addEventListener('play', onPlayVideo, false);
    videoElement.value.addEventListener('pause', onPauseVideo, false);
};

const startNavTimer = () => {
    navTimeout.value = setTimeout(() => {
        isNavVisible.value = false;
    }, controlsTimer);
};

const resetNavTimer = () => {
    isNavVisible.value = true;
    clearTimeout(navTimeout.value);
    startNavTimer();
};

const startLeftArrowTimer = () => {
    leftArrowTimeout.value = setTimeout(() => {
        isLeftArrowVisible.value = false;
    }, controlsTimer);
};

const resetLeftArrowTimer = () => {
    isLeftArrowVisible.value = true;
    clearTimeout(leftArrowTimeout.value);
    startLeftArrowTimer();
};

const startRightArrowTimer = () => {
    rightArrowTimeout.value = setTimeout(() => {
        isRightArrowVisible.value = false;
    }, controlsTimer);
};

const resetRightArrowTimer = () => {
    isRightArrowVisible.value = true;
    clearTimeout(rightArrowTimeout.value);
    startRightArrowTimer();
};

const startThumbTimer = () => {
    thumbTimeout.value = setTimeout(() => {
        isThumbVisible.value = false;
    }, controlsTimer);
};

const resetThumbTimer = () => {
    if (!isPlaying.value) {
        return;
    }

    isThumbVisible.value = true;
    clearTimeout(thumbTimeout.value);
    startThumbTimer();
};

const startCursorTimer = () => {
    cursorTimeout.value = setTimeout(() => {
        isCursorVisible.value = false;
    }, controlsTimer);
};

const resetCursorTimer = () => {
    isCursorVisible.value = true;
    clearTimeout(cursorTimeout.value);
    startCursorTimer();
};

const resetControlsTimer = () => {
    resetNavTimer();
    resetLeftArrowTimer();
    resetRightArrowTimer();
    resetThumbTimer();
    resetCursorTimer();
};

const prev = () => {
    if (!isPlaying.value) {
        return;
    }

    if (hasPrevLabel()) {
        currentLabelIndex.value -= 1;

        // eslint-disable-next-line no-use-before-define
        updateLabelTimer();
    } else {
        isLooping.value = false;
        goToTime.value = prevStep.value.start;
    }
};

const thumbSync = () => {
    const index = _.findIndex(thumbSteps.value, step => step === firstInSequence.value);

    if (index !== -1 && thumb.value && thumb.value.splide) {
        thumb.value.splide.go(index);
        currentThumbIndex.value = index;
    }
};

const thumbClick = index => {
    isLooping.value = false;
    goToTime.value = thumbSteps.value[index].start;
    currentThumbIndex.value = index;
};

const next = () => {
    if (!isPlaying.value) {
        return;
    }

    if (isNextLocked.value) {
        return;
    }

    if (hasNextLabel()) {
        currentLabelIndex.value += 1;

        // eslint-disable-next-line no-use-before-define
        updateLabelTimer();
    } else {
        isLooping.value = false;
        goToTime.value = nextStep.value.start;
    }
};

const startLabelTimer = () => {
    labelTimeout.value = setTimeout(next, labelTimer);
};

const clearLabelTimer = () => {
    clearTimeout(labelTimeout.value);
};

const updateLabelTimer = () => {
    if (!isAutoPlaying.value) {
        return;
    }

    if (!hasLabel()) {
        return;
    }

    if (!isLoopStep.value) {
        return;
    }

    if (!nextStep.value && !hasNextLabel()) {
        return;
    }

    startLabelTimer();
};

const play = () => {
    videoElement.value.play();

    addMetric('play');
};

const autoPlay = () => {
    if (!isPlaying.value) {
        play();
    }

    isAutoPlaying.value = true;

    addMetric('autoplay_on');
};

const stopAutoPlay = () => {
    isAutoPlaying.value = false;

    addMetric('autoplay_off');
};

const pause = () => {
    isThumbVisible.value = false;
    videoElement.value.pause();
    clearLabelTimer();

    addMetric('pause');
};

const backward = () => {
    if (!isPlaying.value) {
        return;
    }

    isLooping.value = false;
    goToTime.value = backwardStep.value.start;

    addMetric('backward');
};

const restart = () => {
    if (!isPlaying.value) {
        return;
    }

    isLooping.value = false;
    goToTime.value = firstInSequence.value.start;

    addMetric('restart');
};

const forward = () => {
    if (!isPlaying.value) {
        return;
    }

    isLooping.value = false;
    goToTime.value = forwardStep.value.start;

    addMetric('forward');
};

const playAudio = () => {
    isAudioEnabled.value = true;
    audioElement.value.play();

    addMetric('audio_on');
};

const stopAudio = () => {
    isAudioEnabled.value = false;
    audioElement.value.pause();

    addMetric('audio_off');
};

const fullscreen = () => {
    if (Screenfull.isEnabled) {
        Screenfull.toggle();
    }
};

const hideTouchControls = () => {
    isNavVisible.value = false;
    clearTimeout(navTimeout.value);
    isThumbVisible.value = false;
    clearTimeout(thumbTimeout.value);
};

const handleMouseAreas = e => {
    const xPercent = e.clientX / window.innerWidth;
    const yPercent = e.clientY / window.innerHeight;

    if (xPercent < 0.2) {
        resetLeftArrowTimer();
    }

    if (xPercent > 0.8) {
        resetRightArrowTimer();
    }

    if (yPercent < 0.2) {
        resetNavTimer();
    }

    if (yPercent > 0.8) {
        resetThumbTimer();
    }
};

const handleTouchAreas = e => {
    resetLeftArrowTimer();
    resetRightArrowTimer();
    resetNavTimer();
    resetThumbTimer();
};

const handleArrows = e => {
    if (isAutoPlaying.value) {
        return;
    }

    if (e.keyCode === 37 && (prevStep.value || hasPrevLabel())) {
        prev();
    } else if (e.keyCode === 39 && (nextStep.value || hasNextLabel())) {
        next();
    }
};

const volumePosition = () => `${volume.value * 100}%`;

const setVolume = e => {
    const bcr = el.value.querySelector('.progress').getBoundingClientRect();

    volume.value = Math.min(Math.max(0, (e.clientX - bcr.left) / bcr.width), 1);
};

const selectLanguage = language => {
    preloadLanguage.value = language;
};

const selectSize = size => {
    currentSize.value = size;
};

const selectTrack = track => {
    currentTrack.value = track;

    if (isAudioPlaying.value) {
        audioElement.value.load();
        audioElement.value.play();
    } else {
        audioElement.value.load();
    }
};

const setupControls = () => {
    document.addEventListener('keypress', resetControlsTimer, false);
    document.addEventListener('keyup', handleArrows, false);

    if (isTouchOnlyDevice) {
        document.addEventListener('mousedown', handleTouchAreas, false);
    } else {
        document.addEventListener('mousedown', handleMouseAreas, false);
        document.addEventListener('mousemove', e => {
            handleMouseAreas(e);
            resetCursorTimer();
        }, false);

        startNavTimer();
        startThumbTimer();
        startCursorTimer();
    }

    startLeftArrowTimer();
    startRightArrowTimer();
};

const hideControls = () => {
    isNavVisible.value = false;
    clearTimeout(navTimeout.value);
    isLeftArrowVisible.value = false;
    clearTimeout(leftArrowTimeout.value);
    isRightArrowVisible.value = false;
    clearTimeout(rightArrowTimeout.value);
};

const sourcePath = size => (size.key === 1080
    ? render.source
    : render[`source_${size.key}`]);

watch(isAutoPlaying, () => {
    if (!hasLabel()) {
        return;
    }

    if (!isLoopStep.value) {
        return;
    }

    if (isAutoPlaying.value) {
        startLabelTimer();
    } else {
        clearLabelTimer();
    }
});

watch(isFullscreen, () => {
    addMetric(`fullscreen_${isFullscreen.value
        ? 'on'
        : 'off'}`);
});

watch(preloadLanguage, () => {
    setTimeout(() => {
        currentAction.value = undefined;
        currentLanguage.value = preloadLanguage.value;

        if (currentLabels.value.length
            && currentLabelIndex.value >= currentLabels.value.length) {
            currentLabelIndex.value = 0;
        }
    }, 500);
});

watch(currentSize, () => {
    isLoaded.value = false;
    isPlaying.value = false;
    isAutoPlaying.value = false;
    isSizeSelected.value = true;
    clearLabelTimer();

    const { currentTime } = videoElement.value;

    videoElement.value.src = sourcePath(currentSize.value);
    videoElement.value.load();
    videoElement.value.currentTime = currentTime;
});

watch(currentIndex, () => {
    if (currentIndex.value > 0) {
        prevStep.value = render.steps[currentIndex.value - 1];

        const prevSteps = _.slice(
            render.steps, 0, currentIndex.value + 1
        );

        firstInSequence.value = _.find(
            prevSteps,
            step => render.steps[currentIndex.value].sequence === step.sequence
        );

        backwardStep.value = _.find(
            prevSteps,
            step => firstInSequence.value.start === step.sequence_end
        );
    } else {
        prevStep.value = undefined;
        backwardStep.value = undefined;
        firstInSequence.value = render.steps[currentIndex.value];
    }

    if (currentIndex.value < render.steps.length - 1) {
        nextStep.value = render.steps[currentIndex.value + 1];

        forwardStep.value = _.find(
            _.slice(render.steps, currentIndex.value + 1),
            step => render.steps[currentIndex.value].sequence_end === step.start
        );
    } else {
        nextStep.value = undefined;
        forwardStep.value = undefined;
    }

    if (isLoopStep.value) {
        isNextLocked.value = false;
        resetRightArrowTimer();
    } else {
        isNextLocked.value = isForceFirstPlay && !isRead;
    }

    clearLabelTimer();

    if (currentLabels.value.length) {
        currentLabelIndex.value = 0;
    } else {
        currentLabelIndex.value = undefined;
    }

    updateLabelTimer();

    sendMetric(app.locale, currentIndex.value);
    addMetric('view');
});

watch(currentAction, () => {
    if (currentAction.value) {
        addVideoAction(currentAction.value.name);
    }
});

watch(firstInSequence, thumbSync);

watch(goToTime, () => {
    if (goToTime.value === -1) {
        resetTimeUpdateTimer();
    }
});

watch(volume, () => {
    audioElement.value.volume = volume.value;
});

onMounted(() => {
    if (render.source) {
        initVideo();
    }

    if (currentTrack.value) {
        initAudio();
    }

    setupControls();

    Screenfull.on('change', () => {
        isFullscreen.value = Screenfull.isFullscreen;
    });

    document.addEventListener('swiped-right', e => {
        if (thumb.value.$el.contains(e.target)) {
            return;
        }

        if (prevStep.value || hasPrevLabel()) {
            prev();
            hideControls();
        }
    });

    document.addEventListener('swiped-left', e => {
        if (thumb.value.$el.contains(e.target)) {
            return;
        }

        if (nextStep.value || hasNextLabel()) {
            next();
            hideControls();
        }
    });
});
</script>
